// src/pages/Dashboard/Admin/Requests/utils/requestsDashboardUtils.js

export const DEFAULT_REQUEST_FILTERS = {
  query: "",
  priority: "",
  status: "",
  sort: "created_desc",
};

export const REQUEST_PRIORITIES = [
  { value: "High", label: "Urgent" },
  { value: "Medium", label: "Moyenne" },
  { value: "Low", label: "Basse" },
];

export const REQUEST_STATUSES = [
  { value: "Pending", label: "En attente" },
  { value: "In Progress", label: "Traitement en cours" },
  { value: "Completed", label: "Terminée" },
];

export const REQUEST_SORT_OPTIONS = [
  { value: "created_desc", label: "Plus récentes" },
  { value: "created_asc", label: "Plus anciennes" },
  { value: "priority_desc", label: "Priorité élevée" },
  { value: "status_asc", label: "Statut" },
  { value: "title_asc", label: "Titre A-Z" },
];

export const QUILL_MODULES = {
  toolbar: [
    [{ header: "1" }, { header: "2" }, { font: [] }],
    [{ size: [] }],
    ["bold", "italic", "underline", "strike", "blockquote"],
    [{ color: [] }, { background: [] }],
    [{ list: "ordered" }, { list: "bullet" }, { indent: "-1" }, { indent: "+1" }],
    ["link", "image", "video"],
    ["clean"],
  ],
};

export const QUILL_FORMATS = [
  "header",
  "font",
  "size",
  "bold",
  "italic",
  "underline",
  "strike",
  "blockquote",
  "color",
  "background",
  "list",
  "bullet",
  "indent",
  "link",
  "image",
  "video",
];

export function safeArray(value) {
  return Array.isArray(value) ? value : [];
}

export function normalizeText(value = "") {
  return String(value)
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .trim();
}

export function stripHtml(html = "") {
  return String(html)
    .replace(/<[^>]*>/g, " ")
    .replace(/&nbsp;/g, " ")
    .replace(/&amp;/g, "&")
    .replace(/&quot;/g, '"')
    .replace(/&#039;/g, "'")
    .replace(/\s+/g, " ")
    .trim();
}

export function sanitizeHtml(html = "") {
  if (typeof window === "undefined") return html;

  const parser = new DOMParser();
  const doc = parser.parseFromString(String(html || ""), "text/html");

  doc.querySelectorAll("script, iframe, object, embed").forEach((node) => {
    node.remove();
  });

  doc.querySelectorAll("*").forEach((node) => {
    [...node.attributes].forEach((attr) => {
      const name = attr.name.toLowerCase();
      const value = String(attr.value || "").toLowerCase();

      if (name.startsWith("on")) {
        node.removeAttribute(attr.name);
      }

      if ((name === "href" || name === "src") && value.startsWith("javascript:")) {
        node.removeAttribute(attr.name);
      }
    });
  });

  return doc.body.innerHTML;
}

export function isQuillEmpty(html) {
  if (!html) return true;
  if (html === "<p><br></p>") return true;

  return stripHtml(html).length === 0;
}

export function truncateText(value = "", max = 180) {
  const text = stripHtml(value || "");

  if (text.length <= max) return text;

  return `${text.slice(0, max).trim()}…`;
}

export function formatDateTimeFR(value) {
  if (!value) return "—";

  const date = new Date(value);

  if (Number.isNaN(date.getTime())) return "—";

  return new Intl.DateTimeFormat("fr-FR", {
    day: "2-digit",
    month: "2-digit",
    year: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  })
    .format(date)
    .replace(",", " -");
}

export function getRequestCreatedAt(request) {
  return (
    request?.created_at ||
    request?.createdAt ||
    request?.date_creation ||
    request?.date ||
    null
  );
}

export function getPriorityMeta(priority) {
  switch (priority) {
    case "High":
      return {
        key: "High",
        label: "Urgent",
        shortLabel: "Urgent",
        badge:
          "bg-rose-50 text-rose-700 ring-rose-200 dark:bg-rose-500/10 dark:text-rose-300 dark:ring-rose-500/20",
        row:
          "bg-rose-50/50 hover:bg-rose-100/60 dark:bg-rose-500/5 dark:hover:bg-rose-500/10",
      };

    case "Low":
      return {
        key: "Low",
        label: "Basse",
        shortLabel: "Basse",
        badge:
          "bg-emerald-50 text-emerald-700 ring-emerald-200 dark:bg-emerald-500/10 dark:text-emerald-300 dark:ring-emerald-500/20",
        row:
          "bg-emerald-50/40 hover:bg-emerald-100/50 dark:bg-emerald-500/5 dark:hover:bg-emerald-500/10",
      };

    case "Medium":
    default:
      return {
        key: "Medium",
        label: "Moyenne",
        shortLabel: "Moy.",
        badge:
          "bg-amber-50 text-amber-700 ring-amber-200 dark:bg-amber-500/10 dark:text-amber-300 dark:ring-amber-500/20",
        row:
          "bg-amber-50/40 hover:bg-amber-100/50 dark:bg-amber-500/5 dark:hover:bg-amber-500/10",
      };
  }
}

export function getStatusMeta(status) {
  switch (status) {
    case "In Progress":
      return {
        key: "In Progress",
        label: "Traitement en cours",
        shortLabel: "En cours",
        badge:
          "bg-sky-50 text-sky-700 ring-sky-200 dark:bg-sky-500/10 dark:text-sky-300 dark:ring-sky-500/20",
      };

    case "Completed":
      return {
        key: "Completed",
        label: "Terminée",
        shortLabel: "Terminée",
        badge:
          "bg-emerald-50 text-emerald-700 ring-emerald-200 dark:bg-emerald-500/10 dark:text-emerald-300 dark:ring-emerald-500/20",
      };

    case "Pending":
    default:
      return {
        key: "Pending",
        label: "En attente",
        shortLabel: "Attente",
        badge:
          "bg-amber-50 text-amber-700 ring-amber-200 dark:bg-amber-500/10 dark:text-amber-300 dark:ring-amber-500/20",
      };
  }
}

export function priorityWeight(priority) {
  if (priority === "High") return 3;
  if (priority === "Medium") return 2;
  if (priority === "Low") return 1;
  return 0;
}

export function getDefaultRequestForm() {
  return {
    title: "",
    description: "",
    priority: "Medium",
  };
}

export function mapRequestToForm(request) {
  if (!request) return getDefaultRequestForm();

  return {
    title: request.title || "",
    description: request.description || "",
    priority: request.priority || "Medium",
  };
}

export function validateRequestForm(formData) {
  const errors = {};

  if (!String(formData.title || "").trim()) {
    errors.title = "Le titre est requis.";
  }

  if (isQuillEmpty(formData.description)) {
    errors.description = "La description est requise.";
  }

  return errors;
}

export function buildCreateRequestPayload(formData, associationId) {
  return {
    association_id: associationId,
    title: String(formData.title || "").trim(),
    description: formData.description || "",
    priority: formData.priority || "Medium",
    status: "Pending",
  };
}

export function buildUpdateRequestPayload(formData) {
  return {
    title: String(formData.title || "").trim(),
    description: formData.description || "",
    priority: formData.priority || "Medium",
  };
}

export function sortRequests(requests, sort = "created_desc") {
  const rows = safeArray(requests).slice();

  return rows.sort((a, b) => {
    if (sort === "title_asc") {
      return String(a?.title || "").localeCompare(String(b?.title || ""), "fr");
    }

    if (sort === "priority_desc") {
      const diff = priorityWeight(b?.priority) - priorityWeight(a?.priority);
      if (diff !== 0) return diff;
    }

    if (sort === "status_asc") {
      return String(a?.status || "").localeCompare(String(b?.status || ""), "fr");
    }

    const dateA = new Date(getRequestCreatedAt(a) || 0).getTime();
    const dateB = new Date(getRequestCreatedAt(b) || 0).getTime();

    if (sort === "created_asc") return dateA - dateB;

    return dateB - dateA;
  });
}

export function filterRequests(requests, filters) {
  const query = normalizeText(filters?.query || "");
  const priority = filters?.priority || "";
  const status = filters?.status || "";

  return sortRequests(requests, filters?.sort).filter((request) => {
    const searchable = normalizeText(
      `${request?.title || ""} ${stripHtml(request?.description || "")}`
    );

    const matchQuery = !query || searchable.includes(query);
    const matchPriority = !priority || request?.priority === priority;
    const matchStatus = !status || request?.status === status;

    return matchQuery && matchPriority && matchStatus;
  });
}

export function calculateRequestsStats(requests) {
  const rows = safeArray(requests);

  let urgent = 0;
  let medium = 0;
  let low = 0;
  let pending = 0;
  let inProgress = 0;
  let completed = 0;

  for (const request of rows) {
    if (request.priority === "High") urgent += 1;
    else if (request.priority === "Low") low += 1;
    else medium += 1;

    if (request.status === "Completed") completed += 1;
    else if (request.status === "In Progress") inProgress += 1;
    else pending += 1;
  }

  return {
    total: rows.length,
    urgent,
    medium,
    low,
    pending,
    inProgress,
    completed,
    open: pending + inProgress,
  };
}